
import React, { useMemo, useState } from 'react';
import Dialog, { DialogHeader, DialogFooter } from '../bootstrap/Dialog.jsx';
import { DATA_TYPE_PROP_MAP } from '../common/AgGrid/index.jsx';
import CustomTextCellEditor from '../common/AgGrid/CustomTextCellEditor.jsx';
import MultiSelectCellEditor from './MultiSelectCellEditor.jsx';
import CellRenderer from '../common/AgGrid/CellRenderer.jsx';

const NO_GROUP = '';

/**
 * This component displays a dialog box for editing an individual row in the datagrid
 * without opening the whole grid for editing. 
 */
const RowEditDialog = ({initialColumnDefs, rowData, setDialogRow, unsavedTransactions, saveRowDialog, singleRowAdd = {}}) => {
  const mode = rowData.id ? 'update' : 'add';
  const [currentRow, setCurrentRow] = useState(rowData);

  const columnDefs = useMemo(() => {
    return (initialColumnDefs || []).reduce((groups, colDef) => {
      if (Array.isArray(colDef.children)) {
        const groupName = colDef.headerName;
        groups[groupName] = groups[groupName] || [];
        groups[groupName] = groups[groupName].concat(colDef.children);
      } else if (colDef.field) {
        groups[NO_GROUP] = groups[NO_GROUP] || [];
        groups[NO_GROUP].push(colDef);
      }
      return groups;
    }, {});

  }, [initialColumnDefs]);

  const close = (e) => {
    // Prevent date pickers from closing dialog
    if (e?.target?.closest?.('.flatpickr-calendar')) return;
    setDialogRow(null);
  }

  const addTransaction = () => {
    unsavedTransactions.current = [{
      [mode]: [{...currentRow, changed: true}]
    }]
  }

  const onValueChange = (colDef, value) => {
    const val = colDef.cellRendererParams.original.column_type === 'boolean' && value === '' ? false : value;
    setCurrentRow(prev => ({...prev, [colDef.field]: val}));
  }

  const resolveEditable = (colDef) => {
    const editable = singleRowAdd?.column_configs?.[colDef.field]?.editable;
    if (editable != null) return editable;
    const column = colDef.cellRendererParams.original;
    let props = {...DATA_TYPE_PROP_MAP[column.column_type]};
    if (props.editable !== false) props.editable = true;
    if (column.formula != null) props.editable = false;
    return props.editable;
  }

  const submit = () => {
    addTransaction();
    saveRowDialog(`single_${mode}`);
  }

  const resolveCellEditor = (colDef) => {
    if (!['string', 'undefined'].includes(typeof colDef.cellEditor)) {
      return colDef.cellEditor;
    }
    const column = colDef.cellRendererParams.original;
    if (column.is_selection || column.column_type === 'boolean') {
      return MultiSelectCellEditor;
    }
    return CustomTextCellEditor;
  }

  const commonParams = colDef => ({ column: colDef, colDef: colDef, data: currentRow});

  const resolveCellStyle = (colDef, editable) => {
    return colDef.cellStyle({value: currentRow[colDef.field], colDef, column: colDef, data: currentRow, editable, editMode: !editable});
  }

  const resolveCellEditorParams = (colDef) => {
    const column = colDef.cellRendererParams.original;
    let value = currentRow[colDef.field];
    if (value?.hasOwnProperty('id')) {
      value = value.id;
    }
    let selectionOptions = null;
    if (column.column_type === 'option_set') {
      selectionOptions = colDef?.cellRendererParams?.getOptions({colDef, data: currentRow}) ?? [];
    } else if (column.is_selection) {
      selectionOptions = column.selection_options.map((option) => {
        if (option.label === null) {
          option.label = option.value;
        }
        return option;
      });
    } else if (column.column_type === 'boolean') {
      const values = [true, false];
      selectionOptions = values.map((value) => ({label: I18n.t(value.toString()), value}));
    }

    return {
      ...commonParams(colDef),
      ...(colDef.cellEditorParams||{}),
      value: value,
      onValueChange: (changedValue) => onValueChange(colDef, changedValue),
      options: selectionOptions,
      isMulti: !['string', 'undefined'].includes(typeof colDef.cellEditor), // Using custom multi selection component for single selection
      autoFocus: false,
      openMenuOnFocus: false,
      cellEditorParams: colDef.cellEditorParams,
    }
  }

  const resolveCellRendererParams = (colDef) => {
    const value = currentRow[colDef.field];
    const valueFormatter = colDef.valueFormatter || colDef.valueGetter;
    const formattedValue = valueFormatter ? valueFormatter({value: value, colDef: colDef, data: currentRow}) : value;
    return {
      ...commonParams(colDef),
      value: formattedValue,
      formattedValue: formattedValue,
      getValue: () => value
    }
  }

  return (
    <div className='datagrid-row-edit-dialog-wrapper'>
      <Dialog
        closeOnOutsideClick={true}
        onClose={close}
        onAdd={submit}
        header={<DialogHeader onClose={close} title={I18n.t(mode === 'add' ? 'add_datagrid_row' : 'edit_datagrid_row')} showBorder={true} />}
        footer={<DialogFooter onClose={close} onAdd={submit} addText={I18n.t('submit')}/>}
        >
        <div className='datagrid-row-edit-dialog' style={{maxWidth: '100%'}}>
          <form>
            {Object.entries(columnDefs).map(([group, columns], index) => {
              const groupNumber = index % 4 + 1;
              const headerClass = group ? `ag-header-group-color-${groupNumber}` : '';

              return (
                <React.Fragment key={group}>
                  <h3>{group}</h3>
                  <div>
                    {columns.map((col, i) => {
                      const colDef = {...col, colId: col.field};
                      const CellEditorComponent = resolveCellEditor(colDef);
                      // const ColumnCellRenderer = col.cellRenderer || CellRenderer;
                      const editable = resolveEditable(colDef) && colDef.field !== 'row_id';
                      const style = resolveCellStyle(colDef, editable);

                      return (
                        <div key={i} className={`dialog-row ${editable ? 'editable' : ''}`}>
                          <label>{col.headerName}</label>
                          <div className={editable ? 'dialog-input-wrapper' : 'form-control'} style={style}>
                            {editable && CellEditorComponent && <CellEditorComponent {...resolveCellEditorParams(colDef)} />}
                            {/*{!editable && <ColumnCellRenderer {...resolveCellRendererParams(colDef)} style={style} />}*/}
                            {!editable && I18n.t('auto_generated')}
                          </div>
                        </div>
                      )
                    })}
                </div>
                </React.Fragment>
              )
            }
            )}
          </form>
        </div>
      </Dialog>
    </div>
  )
}

export default RowEditDialog;